import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Check, ChevronDown } from "lucide-react";
import { FixedButton, MobileShell, TopNav } from "./components";
import { navigate } from "./router";
import { requestApi, setApiToken } from "../shared/api";
import { formatPhone } from "./utils";

const SIGNUP_DRAFT_KEY = "sodamwon-signup-draft";

interface SignupDraft {
  loginId: string;
  password: string;
  name: string;
  phone: string;
  gender: "male" | "female";
  birthDate: string;
}

function readSignupDraft(): Partial<SignupDraft> {
  try { return JSON.parse(sessionStorage.getItem(SIGNUP_DRAFT_KEY) || "{}"); }
  catch { return {}; }
}

export function TbdLogo() {
  return (
    <div className="tbd-logo">
      <img src="/assets/sodamwon-logo-official.png" alt="소담원" />
    </div>
  );
}

export function LoginScreen() {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const url = new URL(window.location.href);
    const handoff = url.searchParams.get("kakaoCode");
    const kakaoError = url.searchParams.get("kakaoError");
    if (kakaoError) setError("카카오 로그인에 실패했습니다. 앱 설정과 동의항목을 확인해 주세요.");
    if (!handoff) return;
    setSubmitting(true);
    requestApi<{ token: string }>(`/auth/kakao/exchange?code=${encodeURIComponent(handoff)}`)
      .then((result) => {
        setApiToken(result.token);
        navigate("home");
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "카카오 로그인에 실패했습니다."))
      .finally(() => setSubmitting(false));
  }, []);
  const invalid = submitted && (!id.trim() || !password);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    setError("");
    if (!id.trim() || !password) return;
    setSubmitting(true);
    try {
      const result = await requestApi<{ token: string }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ loginId: id.trim(), password }),
      });
      setApiToken(result.token);
      navigate("home");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "로그인에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <MobileShell className="login-screen">
      <TbdLogo />
      <form className="login-form" onSubmit={submit} noValidate>
        <div className="login-fields">
          <label className="sr-only" htmlFor="login-id">
            아이디
          </label>
          <input
            id="login-id"
            value={id}
            onChange={(e) => setId(e.target.value)}
            className={invalid && !id ? "is-error" : ""}
            placeholder="아이디를 입력해 주세요."
            autoComplete="username"
          />
          <label className="sr-only" htmlFor="login-password">
            비밀번호
          </label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={invalid && !password ? "is-error" : ""}
            placeholder="비밀번호를 입력해 주세요."
            autoComplete="current-password"
          />
        </div>
        {(invalid || error) && (
          <p className="form-error">{error || "아이디 또는 비밀번호를 확인해 주세요."}</p>
        )}
        <button className="login-submit" type="submit" disabled={submitting}>
          {submitting ? "로그인 중" : "로그인"}
        </button>
        <nav className="login-links" aria-label="계정 메뉴">
          <button type="button" onClick={() => navigate("findId")}>
            아이디 찾기
          </button>
          <span>|</span>
          <button type="button" onClick={() => navigate("findPassword")}>
            비밀번호 찾기
          </button>
          <span>|</span>
          <button type="button" onClick={() => navigate("signupTerms")}>
            회원가입
          </button>
        </nav>
        <div className="login-divider"><span>또는</span></div>
        <a className="kakao-login-button" href="/api/auth/kakao/start" aria-label="카카오로 로그인">
          <img className="kakao-mark" src="/assets/kakao-talk.svg" alt="" aria-hidden="true" />
          카카오로 시작하기
        </a>
      </form>
    </MobileShell>
  );
}

const terms = [
  "[필수] 이용약관 동의",
  "[필수] 개인 정보 수집 및 이용",
  "[필수] 개인 정보 취급 위탁",
  "[선택] 마케팅 정보 활용",
  "[선택] 위치 기반 수집 동의",
];
export function SignupTermsScreen() {
  const [checked, setChecked] = useState<boolean[]>(
    Array(terms.length).fill(false),
  );
  const all = checked.every(Boolean);
  const required = checked[0] && checked[1];
  const toggleAll = () => setChecked(Array(terms.length).fill(!all));
  return (
    <MobileShell className="form-screen has-fixed-action terms-screen">
      <TopNav title="회원가입" fallback="login" />
      <section className="screen-content auth-content">
        <h2>
          서비스 이용을 위해
          <br />
          이용약관 동의가 필요합니다.
        </h2>
        <button
          className={`terms-all ${all ? "is-checked" : ""}`}
          type="button"
          onClick={toggleAll}
        >
          <span className="check-square">
            <Check size={12} />
          </span>
          <strong>약관에 전체 동의합니다.</strong>
        </button>
        <div className="terms-list">
          {terms.map((term, index) => (
            <div className="term-row" key={term}>
              <button
                type="button"
                onClick={() =>
                  setChecked((values) =>
                    values.map((v, i) => (i === index ? !v : v)),
                  )
                }
              >
                <span
                  className={`check-square ${checked[index] ? "is-checked" : ""}`}
                >
                  <Check size={12} />
                </span>
                <span>{term}</span>
              </button>
              <ChevronDown size={18} color="#98a2b3" />
            </div>
          ))}
        </div>
      </section>
      <FixedButton
        disabled={!required}
        onClick={() => navigate("signupProfile")}
      >
        다음
      </FixedButton>
    </MobileShell>
  );
}

export function SignupProfileScreen() {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [birthDate, setBirthDate] = useState("");
  const birthDateRef = useRef<HTMLInputElement>(null);
  const [checkedId, setCheckedId] = useState(false);
  const [checkingId, setCheckingId] = useState(false);
  const [idMessage, setIdMessage] = useState("");
  const valid =
    checkedId &&
    password.length >= 8 &&
    password === passwordConfirm &&
    Boolean(name.trim()) &&
    phone.replace(/\D/g, "").length >= 10 &&
    Boolean(gender) &&
    Boolean(birthDate);
  return (
    <MobileShell className="form-screen has-fixed-action signup-profile-screen">
      <TopNav title="회원가입" fallback="signupTerms" />
      <section className="screen-content auth-content">
        <h2>
          회원님의 기본 정보를
          <br />
          입력해 주세요.
        </h2>
        <div className="field-stack signup-profile-form">
          <div className="form-group">
            <label>아이디</label>
            <div className="inline-field">
              <input
                value={id}
                onChange={(e) => {
                  setId(e.target.value);
                  setCheckedId(false);
                }}
                placeholder="아이디를 입력해 주세요."
              />
              <button
                type="button"
                disabled={checkingId}
                onClick={async () => {
                  setCheckingId(true);
                  setCheckedId(false);
                  setIdMessage("");
                  try {
                    const result = await requestApi<{ valid: boolean; available: boolean }>(`/auth/check-id?loginId=${encodeURIComponent(id.trim())}`);
                    setCheckedId(result.available);
                    setIdMessage(result.available ? "사용 가능한 아이디입니다." : result.valid ? "이미 사용 중인 아이디입니다." : "아이디는 영문, 숫자 8~20자로 입력해주세요.");
                  } catch (reason) {
                    setIdMessage(reason instanceof Error ? reason.message : "중복확인에 실패했습니다.");
                  } finally {
                    setCheckingId(false);
                  }
                }}
              >
                {checkingId ? "확인 중" : "중복확인"}
              </button>
            </div>
            <p className={`field-help ${checkedId ? "ok" : ""}`}>
              {idMessage || "아이디는 영문, 숫자 8~20자 이내로 입력해주세요."}
            </p>
          </div>
          <div className="form-group">
            <label>비밀번호</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력해 주세요."
            />
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="비밀번호를 입력해 주세요."
            />
          </div>
          <div className="form-group">
            <label>이름</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="이름을 입력해 주세요."
            />
          </div>
          <div className="form-group">
            <label>휴대전화 번호</label>
            <input
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              inputMode="tel"
              placeholder="휴대전화 번호를 숫자만 입력해 주세요."
            />
          </div>
          <div className="form-group">
            <label>생년월일</label>
            <div className="signup-date-field" onClick={() => birthDateRef.current?.showPicker?.()}>
              <input
                ref={birthDateRef}
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={birthDate}
                onChange={(event) => setBirthDate(event.target.value)}
                aria-label="생년월일"
              />
              <CalendarDays size={18} aria-hidden="true" />
            </div>
          </div>
          <fieldset className="form-group signup-gender-field">
            <legend>성별</legend>
            <div>
              <button type="button" className={gender === "male" ? "is-active" : ""} onClick={() => setGender("male")}>남성</button>
              <button type="button" className={gender === "female" ? "is-active" : ""} onClick={() => setGender("female")}>여성</button>
            </div>
          </fieldset>
        </div>
      </section>
      <FixedButton disabled={!valid} onClick={() => {
        sessionStorage.setItem(SIGNUP_DRAFT_KEY, JSON.stringify({ loginId: id.trim(), password, name: name.trim(), phone, gender: gender as "male" | "female", birthDate } satisfies SignupDraft));
        navigate("signupEmail");
      }}>
        다음
      </FixedButton>
    </MobileShell>
  );
}

export function SignupEmailScreen() {
  const [local, setLocal] = useState("");
  const [domain, setDomain] = useState("naver.com");
  const [customDomain, setCustomDomain] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [verificationId, setVerificationId] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const email = `${local.trim()}@${domain === "직접입력" ? customDomain.trim() : domain}`;
  return (
    <MobileShell className="form-screen has-fixed-action">
      <TopNav title="회원가입" fallback="signupProfile" />
      <section className="screen-content auth-content">
        <h2>
          마지막으로 가입을 위해
          <br />
          이메일을 인증해 주세요.
        </h2>
        <div className="field-stack">
          <label>
            이메일 <b>*</b>
          </label>
          <div className="email-row">
            <input
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="이메일"
            />
            <span>@</span>
            <div className="select-wrap">
              <select
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              >
                <option>naver.com</option>
                <option>gmail.com</option>
                <option>daum.net</option>
                <option>직접입력</option>
              </select>
              <ChevronDown size={17} />
            </div>
          </div>
          {domain === "직접입력" && (
            <input value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="도메인을 입력해 주세요." />
          )}
          <button
            className="outline-action"
            type="button"
            disabled={!local || !email.includes(".") || working}
            onClick={async () => {
              setWorking(true);
              setError("");
              setVerified(false);
              try {
                const result = await requestApi<{ verificationId: string; devCode?: string }>("/auth/email/send", {
                  method: "POST", body: JSON.stringify({ email, purpose: "signup" }),
                });
                setVerificationId(result.verificationId);
                setSent(true);
                if (result.devCode) setCode(result.devCode);
              } catch (reason) {
                setError(reason instanceof Error ? reason.message : "인증번호 발송에 실패했습니다.");
              } finally { setWorking(false); }
            }}
          >
            {sent ? "인증번호 재발송" : "인증번호 발송"}
          </button>
          {!sent && error && <p className="form-error">{error}</p>}
          {sent && (
            <>
              <label>
                인증번호 <b>*</b>
              </label>
              <div className="inline-field">
                <input
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 8))
                  }
                  inputMode="numeric"
                  placeholder="8자리 인증번호"
                />
                <button
                  type="button"
                  disabled={working}
                  onClick={async () => {
                    setWorking(true);
                    setError("");
                    try {
                      await requestApi("/auth/email/verify", { method: "POST", body: JSON.stringify({ verificationId, code }) });
                      setVerified(true);
                    } catch (reason) {
                      setVerified(false);
                      setError(reason instanceof Error ? reason.message : "인증번호를 확인해 주세요.");
                    } finally { setWorking(false); }
                  }}
                >
                  {working ? "확인 중" : "확인"}
                </button>
              </div>
              <p className={`field-help ${verified ? "ok" : ""}`}>
                {verified
                  ? "이메일 인증이 완료되었습니다."
                  : "인증번호는 3분간 유효합니다."}
              </p>
              {error && <p className="form-error">{error}</p>}
            </>
          )}
        </div>
      </section>
      <FixedButton disabled={!verified || working} onClick={async () => {
        const draft = readSignupDraft();
        setWorking(true);
        setError("");
        try {
          const result = await requestApi<{ token: string }>("/auth/register", {
            method: "POST",
            body: JSON.stringify({ ...draft, email, verificationId }),
          });
          setApiToken(result.token);
          sessionStorage.removeItem(SIGNUP_DRAFT_KEY);
          navigate("signupDone");
        } catch (reason) {
          setError(reason instanceof Error ? reason.message : "회원가입에 실패했습니다.");
        } finally { setWorking(false); }
      }}>
        가입 완료
      </FixedButton>
    </MobileShell>
  );
}

export function SignupDoneScreen() {
  return (
    <MobileShell>
      <TopNav title="회원가입" fallback="login" />
      <div className="complete-state">
        <span className="complete-check">
          <Check size={28} />
        </span>
        <h2>회원가입이 완료되었습니다.</h2>
        <p>소담원과 함께 정성스러운 상품을 만들어 보세요.</p>
        <button type="button" onClick={() => navigate("login")}>
          로그인하러 가기
        </button>
      </div>
    </MobileShell>
  );
}

export function FindAccountScreen({ type }: { type: "id" | "password" }) {
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState("");
  const title = type === "id" ? "아이디 찾기" : "비밀번호 찾기";
  return (
    <MobileShell className="form-screen">
      <TopNav title={title} fallback="login" />
      <section className="screen-content auth-content">
        <h2>
          가입 시 등록한 이메일을
          <br />
          입력해 주세요.
        </h2>
        <div className="field-stack">
          <label>이메일</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="이메일을 입력해 주세요."
          />
          <button
            className="outline-action"
            type="button"
            onClick={() => setSent(Boolean(email))}
          >
            인증번호 발송
          </button>
          {sent && (
            <>
              <label>인증번호</label>
              <input
                inputMode="numeric"
                placeholder="인증번호를 입력해 주세요."
              />
              <button
                className="primary-block"
                type="button"
                onClick={() =>
                  navigate(type === "id" ? "login" : "resetPassword")
                }
              >
                {type === "id" ? "아이디 확인" : "다음"}
              </button>
            </>
          )}
        </div>
      </section>
    </MobileShell>
  );
}

export function ResetPasswordScreen() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const valid = useMemo(() => a.length >= 8 && a === b, [a, b]);
  return (
    <MobileShell className="form-screen has-fixed-action">
      <TopNav title="비밀번호 재설정" fallback="findPassword" />
      <section className="screen-content auth-content">
        <h2>
          새로운 비밀번호를
          <br />
          설정해 주세요.
        </h2>
        <div className="field-stack">
          <label>새 비밀번호</label>
          <input
            type="password"
            value={a}
            onChange={(e) => setA(e.target.value)}
            placeholder="영문+숫자 8~20자"
          />
          <label>새 비밀번호 확인</label>
          <input
            type="password"
            value={b}
            onChange={(e) => setB(e.target.value)}
            placeholder="비밀번호를 다시 입력해 주세요."
          />
        </div>
      </section>
      <FixedButton disabled={!valid} onClick={() => navigate("login")}>
        변경 완료
      </FixedButton>
    </MobileShell>
  );
}
